import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, Plus, Trash2, ArrowRight } from 'lucide-react';
import { programRoutes } from '../data/mock';

const route = (id) => programRoutes.find((r) => r.id === id);
const COMFORT = [
  { id: 'excited', label: 'Loves the water' },
  { id: 'unsure', label: 'A little unsure' },
  { id: 'nervous', label: 'Nervous or fearful' },
];

const toMonths = (c) => (c.unit === 'years' ? Number(c.age) * 12 : Number(c.age));

export function matchProgram(child) {
  const m = toMonths(child);
  if (m < 8) return { id: null, note: 'First Splash starts at 8 months — join its waitlist and we’ll reach out when your little one is ready.', fallback: 'first-splash' };
  if (m < 12) return { id: 'first-splash', note: 'A gentle Parent & Me introduction to the water.' };
  if (m < 36) return { id: 'foundations', note: '20 private lessons over 12 weeks to build real water-safety skills without the rush.' };
  if (m <= 156) {
    if (child.extraSupport) return { id: 'private', note: 'Extra 1:1 or 2:1 attention to help them thrive. Our Group Membership is also a great option once they feel ready.' };
    return { id: 'membership', note: child.comfort === 'nervous' ? 'Max 4 per class with confidence-first coaching — nervous swimmers are very welcome.' : 'Small classes, real skills, monthly membership.' };
  }
  return { id: 'private', note: 'Older swimmers get a fully customized plan in private or semi-private lessons.' };
}

const blank = () => ({ name: '', age: '', unit: 'years', comfort: 'unsure', extraSupport: false });

function ChildRow({ c, i, update, remove, canRemove }) {
  return (
    <div className="rounded-2xl border border-splash-sand p-4 space-y-3" data-testid={`quiz-child-${i}`}>
      <div className="flex gap-2">
        <input value={c.name} onChange={(e) => update(i, { name: e.target.value })} placeholder={`Swimmer ${i + 1} name`} className="input flex-1" data-testid={`quiz-name-${i}`} />
        <button type="button" onClick={() => remove(i)} disabled={!canRemove} aria-label="Remove swimmer" data-testid={`quiz-remove-${i}`} className="w-11 h-11 grid place-items-center rounded-2xl border border-splash-sand text-splash-green/70 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2">
        <input type="number" min="0" step="1" value={c.age} onChange={(e) => update(i, { age: e.target.value })} placeholder="Age" className="input w-28" data-testid={`quiz-age-${i}`} />
        <select value={c.unit} onChange={(e) => update(i, { unit: e.target.value })} className="input flex-1" data-testid={`quiz-age-unit-${i}`}>
          <option value="months">months</option>
          <option value="years">years</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-2">
        {COMFORT.map((o) => (
          <button type="button" key={o.id} onClick={() => update(i, { comfort: o.id })} data-testid={`quiz-comfort-${i}-${o.id}`} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold btn-transition ${c.comfort === o.id ? 'bg-splash-green text-white border-splash-green' : 'border-splash-sand text-splash-green hover:bg-splash-cream'}`}>{o.label}</button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm text-splash-green/80">
        <input type="checkbox" checked={c.extraSupport} onChange={(e) => update(i, { extraSupport: e.target.checked })} data-testid={`quiz-support-${i}`} />
        Needs extra 1:1 attention (focus, sensory, or high anxiety)
      </label>
    </div>
  );
}

function Result({ c, i, onClose }) {
  const m = matchProgram(c);
  const r = route(m.id || m.fallback);
  return (
    <div className="rounded-2xl border border-splash-sand bg-splash-cream/50 p-5" data-testid={`quiz-result-${i}`}>
      <div className="text-xs uppercase tracking-widest text-splash-coral font-semibold">{c.name || `Swimmer ${i + 1}`}</div>
      <div className="mt-1 font-display font-bold text-2xl text-splash-green" data-testid={`quiz-result-program-${i}`}>{m.id ? r.title : 'A little too young — for now'}</div>
      <div className="text-sm text-splash-green/70">{r.ages} &middot; {r.price} {r.per}</div>
      <p className="mt-2 text-sm text-splash-green/85">{m.note}</p>
      <Link to={r.to} onClick={onClose} data-testid={`quiz-result-link-${i}`} className="inline-flex items-center gap-1 mt-3 text-splash-coralDark font-semibold text-sm">See {r.title} <ArrowRight className="w-4 h-4" /></Link>
    </div>
  );
}

export default function FindBestFitQuiz({ open, onClose }) {
  const [children, setChildren] = useState([blank()]);
  const [showResults, setShowResults] = useState(false);
  if (!open) return null;

  const update = (i, patch) => setChildren((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const remove = (i) => setChildren((cs) => cs.filter((_, j) => j !== i));
  const valid = children.every((c) => c.age !== '' && Number(c.age) >= 0);
  const close = () => { setShowResults(false); onClose(); };

  return (
    <div role="dialog" aria-modal="true" data-testid="best-fit-quiz" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={close}>
      <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl border border-splash-sand shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-splash-sand">
          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-splash-coral" /><div className="font-display font-bold text-lg text-splash-green">Find the Best Fit</div></div>
          <button data-testid="quiz-close" onClick={close} aria-label="Close" className="p-1.5 rounded-full hover:bg-splash-cream text-splash-green/70"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 md:p-6 space-y-4">
          {!showResults ? (
            <>
              <p className="text-sm text-splash-green/75">Tell us a little about each swimmer and we&rsquo;ll point you to the right program for their age.</p>
              {children.map((c, i) => <ChildRow key={i} c={c} i={i} update={update} remove={remove} canRemove={children.length > 1} />)}
              <button type="button" onClick={() => setChildren((cs) => [...cs, blank()])} data-testid="quiz-add-child" className="inline-flex items-center gap-1 text-splash-teal font-semibold text-sm"><Plus className="w-4 h-4" /> Add another swimmer</button>
              <button type="button" disabled={!valid} onClick={() => setShowResults(true)} data-testid="quiz-see-results" className="w-full rounded-full bg-splash-coral hover:bg-splash-coralDark disabled:opacity-50 text-white py-3.5 font-semibold btn-transition">See our recommendation</button>
            </>
          ) : (
            <>
              {children.map((c, i) => <Result key={i} c={c} i={i} onClose={close} />)}
              {children.length > 1 && <p className="text-xs text-splash-green/70" data-testid="quiz-sibling-note">Enrolling siblings in Group Membership? A $20 sibling discount is applied after enrollment.</p>}
              <div className="flex flex-wrap gap-3 pt-2">
                <button type="button" onClick={() => setShowResults(false)} data-testid="quiz-back" className="rounded-full border border-splash-sand px-5 py-2.5 text-sm font-semibold text-splash-green">Edit answers</button>
                <Link to="/book" onClick={close} data-testid="quiz-book-call" className="rounded-full bg-splash-green text-white px-5 py-2.5 text-sm font-semibold">Talk to Coach Abrie</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
